<template>
  <AdminAssetTable
    :config="locationsConfig"
    :items="locationsData"
    :users="users"
    :api-service="locationsApiService"
    @refresh="loadLocations"
    @created="handleLocationCreated"
    @updated="handleLocationUpdated"
    @deleted="handleLocationDeleted"
  />
</template>

<script setup lang="ts">
import { locations } from '@/api/locations';
import type { Entity } from '@/api/types/core/common';
import type { User } from '@/api/types/core/users';
import type { Location } from '@/api/types/entities/location/types';
import { fetchAllUsers } from '@/api/users';
import AdminAssetTable from '@/components/admin/AdminAssetTable.vue';
import { ADMIN_ASSET_CONFIGS } from '@/configs/adminAssetConfigs';
import { onMounted, ref } from 'vue';

// Configuration
const locationsConfig = ADMIN_ASSET_CONFIGS.locations;

// État
const locationsData = ref<Location[]>([]);
const users = ref<User[]>([]);
const loading = ref(false);

// Service API adapté au format attendu par AdminAssetTable
const locationsApiService = {
  async getAll(): Promise<Location[]> {
    return await locations.getAllAdminWithDeleted();
  },
  
  async create(data: Record<string, unknown>): Promise<Location> {
    return await locations.createLocation(data as any);
  },
  
  async update(uuid: string, data: Record<string, unknown>): Promise<Location> {
    return await locations.updateLocation(uuid, data as any);
  },
  
  async delete(uuid: string): Promise<void> {
    return await locations.deleteLocation(uuid);
  },
  async hardDelete(uuid: string): Promise<void> {
    return await locations.hardDeleteLocation(uuid);
  },
  async restore(uuid: string): Promise<void> {
    await locations.restoreLocation(uuid);
  }
};

// Méthodes
const loadLocations = async (): Promise<void> => {
  loading.value = true;
  try {
    locationsData.value = await locationsApiService.getAll();
  } catch (error) {
    console.error('Erreur lors du chargement des emplacements:', error);
  } finally {
    loading.value = false;
  }
};

const loadUsers = async (): Promise<void> => {
  try {
    users.value = await fetchAllUsers();
  } catch (error) {
    console.error('Erreur lors du chargement des utilisateurs:', error);
  }
};

// Gestionnaires d'événements
const handleLocationCreated = (location: Entity): void => {
  locationsData.value.push(location as Location);
};

const handleLocationUpdated = (updatedLocation: Entity): void => {
  const index = locationsData.value.findIndex((a: Location): boolean => a.uuid === updatedLocation.uuid);
  if (index !== -1) {
    locationsData.value[index] = updatedLocation as Location;
  }
};

const handleLocationDeleted = (uuid: string): void => {
  locationsData.value = locationsData.value.filter((a: Location): boolean => a.uuid !== uuid);
};

// Lifecycle
onMounted(async (): Promise<void> => {
  await Promise.all([
    loadLocations(),
    loadUsers()
  ]);
});
</script>
  